"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
var splitio_1 = require("@splitsoftware/splitio");
var react_1 = __importStar(require("react"));
exports.defaultTreatment = {
    config: null,
    treatment: 'control',
};
/**
 * Creating a React.Context with default values.
 */
exports.SplitContext = react_1.createContext({
    client: null,
    factory: null,
    isReady: false,
    lastUpdate: 0,
});
/**
 * SplitProvider will initialize client and listen for events that will set things up.
 * Make sure SplitProvider is wrapper your entire app.
 * @see {@link https://help.split.io/hc/en-us/articles/360020448791-JavaScript-SDK}
 * @param {ISplitProviderProps} props
 * @returns {React.Component}
 *
 * @example
 *
 *  <SplitProvider config={SDK_CONFIG_OBJECT}>
 *    <App />
 *  </SplitProvider>
 */
var SplitProvider = function (_a) {
    var config = _a.config, children = _a.children, onImpression = _a.onImpression;
    var _b = react_1.useState({
        client: null,
        factory: null,
    }), _c = _b[0], factory = _c.factory, client = _c.client, setProvider = _b[1];
    var _d = react_1.useState({
        isReady: false,
        lastUpdate: 0,
    }), _e = _d[0], isReady = _e.isReady, lastUpdate = _e.lastUpdate, setUpdated = _d[1];
    // Handle impression listener separately from config.
    // - Allows us to use the onImpression property
    // - And because the listener function is ignored from JSON.stringify,
    //   so changing that in the config wouldn't hook up the new function.
    // - Because of this ^ the function can change without us having to recreate the client.
    var handleImpression = react_1.useCallback(function (data) {
        if (onImpression) {
            onImpression(data);
        }
        if (config.impressionListener && config.impressionListener.logImpression) {
            config.impressionListener.logImpression(data);
        }
    }, []);
    // Determine whether config has changed which would require client to be recreated.
    // Convert config object to string so it works with the identity check ran with useEffect's dependency list.
    // We memoize this so if the user has memoized their config object we don't call JSON.stringify needlessly.
    // We also freeze the config object here so users know modifying it is not what they want to do.
    var configHash = react_1.useMemo(function () { return JSON.stringify(deepFreeze(config)); }, [
        config,
    ]);
    react_1.useEffect(function () {
        // Reset state when re-creating the client after config modification
        if (isReady || lastUpdate > 0) {
            setUpdated({ isReady: false, lastUpdate: 0 });
        }
        var sdkConfig = __assign({}, config, { 
            /** @link https://help.split.io/hc/en-us/articles/360020448791-JavaScript-SDK#2-instantiate-the-sdk-and-create-a-new-split-client */
            impressionListener: {
                logImpression: handleImpression,
            } });
        /**
         * Instantiating a new factory
         * @link https://help.split.io/hc/en-us/articles/360020448791-JavaScript-SDK
         */
        var nextFactory = splitio_1.SplitFactory(sdkConfig);
        var nextClient = nextFactory.client();
        setProvider({
            client: nextClient,
            factory: nextFactory,
        });
        // Only make state changes if component is mounted.
        // https://github.com/facebook/react/issues/14369#issuecomment-468267798
        var isMounted = true;
        var updateListener = function () {
            if (isMounted) {
                setUpdated({ isReady: true, lastUpdate: Date.now() });
            }
        };
        nextClient.on(nextClient.Event.SDK_READY, updateListener);
        nextClient.on(nextClient.Event.SDK_UPDATE, updateListener);
        return function () {
            isMounted = false;
            if (client) {
                client.destroy();
            }
        };
    }, [configHash]);
    // memoize child context to prevent unnecessary re-renders with object's identity changing
    var context = react_1.useMemo(function () { return ({
        client: client,
        factory: factory,
        isReady: isReady,
        lastUpdate: lastUpdate,
    }); }, [factory, client, isReady, lastUpdate]);
    return (react_1.default.createElement(exports.SplitContext.Provider, { value: context }, children));
};
exports.default = SplitProvider;
var deepFreeze = function (object) {
    if (Object.isFrozen(object)) {
        return object;
    }
    // Freeze properties before freezing self
    var propNames = Object.getOwnPropertyNames(object);
    for (var _i = 0, propNames_1 = propNames; _i < propNames_1.length; _i++) {
        var name_1 = propNames_1[_i];
        var value = object[name_1];
        if (value && typeof value === 'object') {
            object[name_1] = deepFreeze(value);
        }
    }
    return Object.freeze(object);
};
//# sourceMappingURL=SplitProvider.js.map